import { useMemo, useState, type ReactNode } from 'react'
import type { Idea } from '../api'
import { WritingRow } from '../components/IdeaRow'
import { PenIcon, PlusIcon } from '../components/icons'
import { SeriesFilter, seriesCounts, useSeriesView } from '../components/SeriesFilter'
import { StatusIcon } from '../components/StatusIcon'
import { loadIdeas, useIdeas } from '../state/ideas'
import { openQuickAdd } from './QuickAdd'
import './pipeline.css'

/**
 * LinkedIn, as a writing list: what is still to write, and what is posted.
 *
 * Martin writes every post himself, so this is a list of topics with the
 * first line of the notes he writes from, and a ring in front to tick once
 * the post is up. It reads only where the topic is posted: a reel going out
 * on the same subject never ticks the post.
 */
export function groupForWriting(ideas: Idea[]): { toWrite: Idea[]; posted: Idea[]; killed: Idea[] } {
  const onLinkedIn = ideas.filter((idea) => idea.channels.includes('linkedin'))
  const isPosted = (idea: Idea) => idea.posted_to.includes('linkedin')
  return {
    toWrite: onLinkedIn
      .filter((idea) => !isPosted(idea) && idea.status !== 'killed')
      .sort((a, b) => b.created_at.localeCompare(a.created_at)),
    posted: onLinkedIn.filter(isPosted).sort((a, b) => b.updated_at.localeCompare(a.updated_at)),
    killed: onLinkedIn.filter((idea) => !isPosted(idea) && idea.status === 'killed'),
  }
}

export function LinkedIn() {
  const { ideas, phase, error } = useIdeas()
  const series = useSeriesView()
  const [showKilled, setShowKilled] = useState(false)
  const topics = useMemo(() => ideas.filter((idea) => idea.channels.includes('linkedin')), [ideas])
  const shown = series === 'all' ? topics : topics.filter((idea) => idea.series === series)
  const { toWrite, posted, killed } = groupForWriting(shown)
  const row = (idea: Idea) => <WritingRow key={idea.id} idea={idea} />

  return (
    <main className="screen writing">
      <header className="pipeline-head">
        <h1 className="pipeline-title">LinkedIn</h1>
        <SeriesFilter counts={seriesCounts(topics.filter((idea) => idea.status !== 'killed'))} />
      </header>

      {phase === 'error' && (
        <div className="empty">
          <p>{error ?? 'Could not load ideas.'}</p>
          <button type="button" className="button" onClick={() => void loadIdeas()}>
            Try again
          </button>
        </div>
      )}

      {phase === 'ready' && topics.length === 0 && (
        <div className="empty channel-empty">
          <PenIcon size={28} />
          <p>No LinkedIn topics yet. Log one here and write it when you are ready.</p>
          <button type="button" className="button" onClick={() => openQuickAdd('idea', 'linkedin')}>
            <PlusIcon size={18} />
            New topic
          </button>
        </div>
      )}

      {phase === 'ready' && topics.length > 0 && (
        <>
          <Group label="To write" icon={<PenIcon size={15} />} count={toWrite.length} onAdd={() => openQuickAdd('idea', 'linkedin')}>
            {toWrite.map(row)}
          </Group>
          <Group label="Posted" icon={<StatusIcon status="published" size={14} />} count={posted.length}>
            {posted.map(row)}
          </Group>
          {killed.length > 0 && (
            <button type="button" className="killed-toggle" aria-expanded={showKilled} onClick={() => setShowKilled((was) => !was)}>
              {showKilled ? 'Hide killed' : 'Show killed'}
              <span className="mono">{killed.length}</span>
            </button>
          )}
          {showKilled && (
            <Group label="Killed" icon={<StatusIcon status="killed" size={14} />} count={killed.length}>
              {killed.map(row)}
            </Group>
          )}
        </>
      )}
    </main>
  )
}

function Group({
  label,
  icon,
  count,
  onAdd,
  children,
}: {
  label: string
  icon: ReactNode
  count: number
  onAdd?: () => void
  children: ReactNode
}) {
  const id = `group-${label.toLowerCase().replace(/\W+/g, '-')}`
  return (
    <section className="section" data-empty={count === 0 || undefined} aria-labelledby={id}>
      <div className="section-head">
        <h2 id={id} className="section-heading">
          <span className="section-toggle">
            <span className="section-icon">{icon}</span>
            <span className="section-label">{label}</span>
            <span className="section-count mono">{count}</span>
          </span>
        </h2>
        {onAdd && (
          <button type="button" className="icon-button section-add" aria-label="New LinkedIn topic" onClick={onAdd}>
            <PlusIcon size={18} />
          </button>
        )}
      </div>
      {count > 0 && <ul className="idea-list">{children}</ul>}
    </section>
  )
}
